import { useEffect, useState } from 'react';
import type { OrderPage, OrderStats } from '@/api/types';
import { api } from '@/api/client';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { orderStatus } from '@/lib/status';
import { useFormFactor } from '@/lib/formFactor';
import type { Filters, ViewKey } from './api';
import s from './FilterPanel.module.css';

type Opt = { value: string; label: string; count?: number };
const STOCK_OPTIONS = [
  { value: 'READY', label: '配货齐全' },
  { value: 'PARTIAL', label: '部分可发' },
  { value: 'SHORTAGE', label: '缺货' },
  { value: 'COMPLETED', label: '无待发' },
] as const;

/**
 * 更多筛选：手机是全高底部 sheet，iPad / 桌面是右侧抽屉（文档 23 §6）。
 * 每组单选（老接口每个参数只收一个值）；保留原稿的「全部」复位项。
 */
export function FilterPanel({ value, stats, onApply, onClose, sheet, view, viewQuery, keyword }: { value: Filters; stats?: OrderStats; onApply: (f: Filters) => void; onClose: () => void; sheet: boolean; view: ViewKey; viewQuery: Record<string, string>; keyword: string }) {
  const ff = useFormFactor();
  const tablet = !sheet && ff === 'tablet';
  const [f, setF] = useState<Filters>(value);
  const [preview, setPreview] = useState<{ total?: number; loading: boolean }>({ loading: true });
  const [stockCounts, setStockCounts] = useState<Record<string, number>>({});
  const [datePreset, setDatePreset] = useState<'none' | 'recent' | 'month' | 'lastMonth' | 'custom'>('none');
  const badDates = Boolean(f.dateFrom && f.dateTo && f.dateFrom > f.dateTo);
  const previewPath = view === 'tasks' ? '/api/v2/orders/tasks' : f.dateFrom || f.dateTo ? '/api/v2/orders/ledger' : '/api/orders';
  const previewKey = JSON.stringify({ ...viewQuery, ...f, dateField: f.dateFrom || f.dateTo ? f.dateField || 'customerOrderDate' : undefined, keyword: keyword || undefined, pageSize: 1 });
  useEffect(() => {
    if (badDates) { setPreview({ loading: false }); return; }
    const controller = new AbortController();
    setPreview({ loading: true });
    const timer = window.setTimeout(() => {
      void api.get<OrderPage>(previewPath, JSON.parse(previewKey), controller.signal)
        .then((result) => setPreview({ total: result.pagination.total, loading: false }))
        .catch((error: Error) => { if (error.name !== 'AbortError') setPreview({ loading: false }); });
    }, 180);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [badDates, previewKey, previewPath]);
  useEffect(() => {
    if (!sheet && !tablet) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const query = JSON.parse(previewKey) as Record<string, string | number | undefined>;
      void Promise.all(STOCK_OPTIONS.map(async ({ value }) => {
        const page = await api.get<OrderPage>(previewPath, { ...query, page: 1, pageSize: 1, includeStock: 1, stockStatus: value }, controller.signal);
        return [value, page.pagination.total] as const;
      })).then((counts) => { if (!controller.signal.aborted) setStockCounts(Object.fromEntries(counts)); })
        .catch((error: Error) => { if (error.name !== 'AbortError' && !controller.signal.aborted) setStockCounts({}); });
    }, 180);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [previewKey, previewPath, sheet, tablet]);
  const applyDateRange = (preset: 'recent' | 'month' | 'lastMonth') => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const end = new Date(start);
    if (preset === 'recent') start.setDate(start.getDate() - 6);
    if (preset === 'month') start.setDate(1);
    if (preset === 'lastMonth') {
      start.setDate(1);
      start.setMonth(start.getMonth() - 1);
      end.setDate(0);
    }
    const iso = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setF({ ...f, dateField: f.dateField || 'customerOrderDate', dateFrom: iso(start), dateTo: iso(end) });
    setDatePreset(preset);
  };
  const groups: { key: keyof Filters; title: string; opts: Opt[] }[] = [
    { key: 'status', title: '订单状态', opts: (Object.keys(orderStatus) as (keyof typeof orderStatus)[]).map((k) => ({ value: k, label: orderStatus[k].label, count: stats?.statuses.find((x) => x.value === k)?.count ?? 0 })) },
    { key: 'assignment', title: '分配状态', opts: [{ value: 'assigned', label: '已分配', count: stats?.assigned }, { value: 'unassigned', label: '未分配', count: stats?.unassigned }] },
    { key: 'stockStatus', title: '库存状态', opts: STOCK_OPTIONS.map((o) => ({ ...o, count: stockCounts[o.value] })) },
    { key: 'exceptionStatus', title: '异常', opts: [{ value: 'has', label: '有异常', count: stats?.exceptions?.has }, { value: 'open', label: '未闭环', count: stats?.exceptions?.open }, { value: 'PENDING', label: '待处理', count: stats?.exceptions?.PENDING }, { value: 'PROCESSING', label: '处理中', count: stats?.exceptions?.PROCESSING }, { value: 'RESOLVED', label: '已处理', count: stats?.exceptions?.RESOLVED }, { value: 'CLOSED', label: '已关闭', count: stats?.exceptions?.CLOSED }] },
    { key: 'invoiceStatus', title: '开票状态', opts: [{ value: 'NOT_REQUIRED', label: '无需开票' }, { value: 'PENDING', label: '待开票' }, { value: 'ISSUED', label: '已开票' }].map((o) => ({ ...o, count: stats?.invoiceStatuses.find((x) => x.value === o.value)?.count })) },
    { key: 'changeState', title: '变更状态', opts: [{ value: 'changed', label: '有变更', count: stats?.orderChanges.changed }, { value: 'pendingReview', label: '待审核', count: stats?.orderChanges.pendingReview }, { value: 'pendingAcknowledgement', label: '待知晓', count: stats?.orderChanges.pendingAcknowledgement }, { value: 'acknowledged', label: '已知晓', count: stats?.orderChanges.acknowledged }] },
    { key: 'shop', title: '店铺', opts: (stats?.shops || []).map((x) => ({ value: x.value, label: x.label, count: x.count })) },
  ];
  const orderedGroups = tablet || sheet ? [groups[0], groups[2], groups[6], groups[1], groups[3], groups[4], groups[5]] : groups;
  const picked = Object.values(f).filter(Boolean).length;
  const dateSection = <section className={s.sec}>
    <h4>日期{(f.dateFrom || f.dateTo) && <span>已选范围</span>}</h4>
    <div className={s.datePresets}>
      <button type="button" className={datePreset === 'recent' ? s.on : ''} aria-pressed={datePreset === 'recent'} onClick={() => applyDateRange('recent')}>近 7 天</button>
      <button type="button" className={datePreset === 'month' ? s.on : ''} aria-pressed={datePreset === 'month'} onClick={() => applyDateRange('month')}>本月</button>
      <button type="button" className={datePreset === 'lastMonth' ? s.on : ''} aria-pressed={datePreset === 'lastMonth'} onClick={() => applyDateRange('lastMonth')}>上月</button>
      <button type="button" className={datePreset === 'custom' ? s.on : ''} aria-pressed={datePreset === 'custom'} onClick={() => setDatePreset('custom')}>自定义</button>
    </div>
    <div className={s.dateFields}>
      <label>按<select value={f.dateField || 'customerOrderDate'} onChange={(event) => setF({ ...f, dateField: event.target.value })}><option value="customerOrderDate">下单日期</option><option value="deliveryDate">交货期</option></select></label>
      <label>起<input type="date" value={f.dateFrom || ''} onChange={(event) => { setDatePreset('custom'); setF({ ...f, dateField: f.dateField || 'customerOrderDate', dateFrom: event.target.value || undefined }); }} /></label>
      <label>止<input type="date" value={f.dateTo || ''} min={f.dateFrom || undefined} onChange={(event) => { setDatePreset('custom'); setF({ ...f, dateField: f.dateField || 'customerOrderDate', dateTo: event.target.value || undefined }); }} /></label>
    </div>
    {f.dateFrom && f.dateTo && f.dateFrom > f.dateTo && <p className={s.hint}>结束日期不能早于起始日期。</p>}
  </section>;
  return (
    <div className={s.scrim} onClick={onClose}>
      <aside className={sheet ? s.sheet : `${s.drawer} ${tablet ? s.tabletDrawer : ''}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="筛选">
        {sheet && <div className={s.grab} />}
        <header className={s.hd}>
          <button className={s.x} onClick={onClose} aria-label="关闭"><Icon name="x" size={20} /></button>
          <b>{sheet || tablet ? '筛选' : '更多筛选'}</b>
          <button className={s.reset} onClick={() => { setF({}); setDatePreset('none'); }}>重置</button>
        </header>
        <div className={s.bd}>
          {orderedGroups.map((g) => g.opts.length > 0 && (
            <div key={g.key}>
              {(tablet || sheet) && g.key === 'assignment' && dateSection}
              <section className={s.sec}>
                <h4>{g.title}{f[g.key] && <span>已选 1</span>}</h4>
                <div className={s.chips}>
                  {!tablet && !sheet && <button className={`${s.chip} ${!f[g.key] && !(g.key === 'status' && f.invoiceStatus === 'ISSUED') ? s.on : ''}`} aria-pressed={!f[g.key] && !(g.key === 'status' && f.invoiceStatus === 'ISSUED')} onClick={() => setF({ ...f, [g.key]: undefined, ...(g.key === 'status' && f.invoiceStatus === 'ISSUED' ? { invoiceStatus: undefined } : {}) })}>全部{g.key === 'status' && stats?.all !== undefined && <i>{stats.all}</i>}</button>}
                  {g.opts.map((o) => (
                    <button key={o.value} className={`${s.chip} ${f[g.key] === o.value ? s.on : ''}`}
                      aria-pressed={f[g.key] === o.value}
                      onClick={() => setF({ ...f, [g.key]: f[g.key] === o.value ? undefined : o.value })}>
                      {o.label}{o.count != null && <i>{o.count}</i>}
                    </button>
                  ))}
                  {g.key === 'status' && !tablet && !sheet && <button className={`${s.chip} ${f.invoiceStatus === 'ISSUED' && !f.status ? s.on : ''}`} aria-pressed={f.invoiceStatus === 'ISSUED' && !f.status} onClick={() => setF({ ...f, status: undefined, invoiceStatus: 'ISSUED' })}>已开票{stats?.invoiceStatuses.find((item) => item.value === 'ISSUED') && <i>{stats.invoiceStatuses.find((item) => item.value === 'ISSUED')!.count}</i>}</button>}
                </div>
              </section>
            </div>
          ))}
          {!(tablet || sheet) && dateSection}
        </div>
        <footer className={s.ft}>
          {!sheet && !tablet && <button type="button" className={s.footerReset} onClick={() => setF({})}>重置</button>}
          <Button variant="primary" size="lg" block disabled={badDates} disabledReason="结束日期不能早于起始日期" onClick={() => onApply({ ...f, dateField: f.dateFrom || f.dateTo ? f.dateField || 'customerOrderDate' : undefined })}>{sheet || tablet ? preview.loading ? '查询结果…' : preview.total !== undefined ? `查看 ${preview.total} 条结果` : `应用${picked ? `（${picked} 项）` : ''}` : '应用筛选'}</Button>
        </footer>
      </aside>
    </div>
  );
}
